import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getNoticiaBySlug } from '../../config/services/strapi/noticias';
import './detalle-noticia-historica.css';
import TextSkeleton from '../../components/Skeleton/TextSkeleton';
import ImageSkeleton from '../../components/Skeleton/ImageSkeleton';

const DetalleNoticiaHistorica = () => {
  const { slug } = useParams<{ slug: string }>();
  const [noticia, setNoticia] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const shareUrl = typeof window !== 'undefined' ? encodeURIComponent(window.location.href) : '';
  const shareTitle = noticia ? encodeURIComponent(noticia.descripcionImage?.[0]?.children?.[0]?.text || "Noticia") : '';

  useEffect(() => {
    async function loadData() {
      if (slug) {
        const data = await getNoticiaBySlug(slug);
        setNoticia(data);
        setLoading(false);
      }
    }
    loadData();
  }, [slug]);

  useEffect(() => {
    if (loading || !noticia) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        contentRef.current?.classList.add('is-visible');
      }
    }, { threshold: 0.6 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [loading, noticia]);

  if (loading) {
    return (
      <div className="loading-state">
        <TextSkeleton />
        <ImageSkeleton className="mt-4 h-64" />
      </div>
    );
  }

  if (!noticia) return <div className="error-state">Noticia no encontrada</div>;

  const tituloHeader = noticia.descripcionImage?.[0]?.children?.[0]?.text;
  const fechaPublicacion = noticia.anoImage;
  const imagenLateral = noticia.image?.url;

  const getTexto = (index: number) => noticia.descripcionDetalle?.[index]?.children?.[0]?.text || "";

  return (
    <>
      <section ref={sectionRef} className="header-noticia-simple-historica">
        <div ref={contentRef} className="header-noticia-content-historica">
          <div className="header-noticia-info-historica">
            <span className="header-date-historica">{fechaPublicacion}</span>
            <span className="header-separator-historica">|</span>
            <span className="header-category-historica">In <Link to="/noticias">Noticias</Link></span>
          </div>
          <h1 className="header-noticia-title-historica">{tituloHeader}</h1>
        </div>
        <a href="#proxima-seccion" className="header-scrolldown-historica">
          <i className="fa-solid fa-angle-down"></i>
        </a>
      </section>

      <section id="proxima-seccion" className="section-cuerpo-noticia-historica">
        <div className="contenedor-lectura-historica">
          <div className="fila-contenido-historica">

            <div className="columna-texto-historica">
              <h2 className="titulo-noticia-secundario-historica">
                {getTexto(0)}
              </h2>

              <p className="fuente-datos-historica">
                <b>{getTexto(1)}</b><br />
                <b>{getTexto(2)}</b>
              </p>

              <div className="texto-descripcion-historica">
                  {noticia.descripcionDetalle?.slice(4).map((block: any, i: number) => {
                    const txt = block.children?.[0]?.text;
                    const key = `detalle-${noticia.id || 'noticia'}-${i + 4}`;
                    return txt ? <p key={key}>{txt}</p> : null;
                  })}
              </div>
            </div>

            <div className="columna-imagen-historica">
              <img src={imagenLateral} alt={tituloHeader} className="imagen-fluida-historica" />
            </div>

          </div>
        </div>
      </section>

      {/* Footer Social */}
      <section className="section-compartir-footer-fabrica">
        <div className="post-footer-compartir-fabrica">
          <div className="footer-compartir-inner-fabrica">
            <div className="social load">
              <ul>
                {/* Facebook */}
                <li className="social-facebook">
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-facebook-f"></i>
                  </a>
                </li>

                {/* X (Twitter) */}
                <li className="social-twitter">
                  <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`} target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-x-twitter"></i>
                  </a>
                </li>

                {/* Threads */}
                <li className="social-threads">
                  <a href={`https://www.threads.net/intent/post?text=${shareTitle}%20${shareUrl}`} target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-threads"></i>
                  </a>
                </li>

                {/* Pinterest */}
                <li className="social-pinterest">
                  <a href={`https://pinterest.com/pin/create/button/?url=${shareUrl}&description=${shareTitle}`} target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-pinterest-p"></i>
                  </a>
                </li>

                {/* LinkedIn */}
                <li className="social-linkedin">
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-linkedin-in"></i>
                  </a>
                </li>

                {/* WhatsApp */}
                <li className="social-whatsapp">
                  <a href={`https://api.whatsapp.com/send?text=${shareTitle}%20${shareUrl}`} target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-whatsapp"></i>
                  </a>
                </li>

                {/* Bluesky */}
                <li className="social-bluesky">
                  <a href={`https://bsky.app/intent/compose?text=${shareTitle}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" className="icon-bluesky">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 10.8c-1.3-1.8-3.3-4.3-5.5-5.3-2.2-1-4.5-.4-4.5 2.1 0 1.2.7 4.1 1.2 5.3.7 1.6 2.5 2.1 4.1 1.8-2 .4-4.4 1.3-4.4 3.9 0 2.5 2.2 3 4.5 2 2.2-1 4.2-3.5 5.5-5.3 1.3 1.8 3.3 4.3 5.5 5.3 2.2 1 4.5.5 4.5-2 0-2.6-2.4-3.5-4.4-3.9 1.6.3 3.4-.2 4.1-1.8.5-1.2 1.2-4.1 1.2-5.3 0-2.5-2.3-3.1-4.5-2.1-2.2 1-4.2 3.5-5.5 5.3Z" />
                    </svg>
                  </a>
                </li>

                {/* Xing */}
                <li className="social-xing">
                  <a href={`https://www.xing.com/spi/shares/new?url=${shareUrl}`} target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-xing"></i>
                  </a>
                </li>

                {/* Email */}
                <li className="social-paper-plane">
                  <a href={`mailto:?subject=${shareTitle}&body=${shareUrl}`}>
                    <i className="fa-solid fa-paper-plane"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DetalleNoticiaHistorica;